import React from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { Button, Dialog, Checkbox, TextArea, Uploader } from '@app/components';

import {
  ModalContainer,
  ChecklistText,
  Footer,
  ChecklistButtonContainer,
  ExemptButtonContainer,
  CancelButton,
  SavingIndicator,
  TaxValue,
  VerifyCard,
  VerifyBox,
  TaxText,
  EqualValue,
  ExemptContainer,
  ExemptTextArea,
  InputCard,
  ExemptBox,
  ChecklistPrimaryText,
  ExemptInfoBox,
} from '@app/modules/transaction-details/transactions-details-components';

import { ActivityLogInfo, PaymentProps } from '@app/types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { primaryColor } from '@app/styles';
import TransactionlogoUrl from '@app/assets/images/Transactionblack.svg';
import { useUpdateChecklistMutation } from '@app/graphql';
import { taxCalculation } from '@app/utils/tax-calculation';

type ExemptionDataInfo = {
  id: string;
  checked: boolean;
  exemption?: string;
};

type Props = {
  payments: PaymentProps[];
  taxExemption: string;
  onModalClose?: any;
  userFirebaseId: string;
  taxInst: string;
  taxRate: string;
  purchasePrice: string;
  status?: string;
  loadChecklistData: (checklistData: any, pendingTasks: string) => void;
  getActivityLogData: (e: ActivityLogInfo[]) => void;
  exemptionData?: any;
};
const taxRateCalc = (taxRate: string) => {
  const taxRateTemp = taxRate.replace(/[&\\#,+()$~%'":*?<>{}]/g, '');
  return parseFloat(taxRateTemp) * 0.001;
};

let activityLog: ActivityLogInfo[] = [];

export default function VerifyTax(props: Props) {
  const {
    onModalClose,
    taxExemption,
    userFirebaseId,
    taxInst,
    taxRate,
    purchasePrice,
    loadChecklistData,
    status,
    getActivityLogData,
    exemptionData,
    payments,
  } = props;
  const { transactionId }: any = useParams();
  const [updateCheckList] = useUpdateChecklistMutation();
  const [showExemptions, updateExemptions] = React.useState(false);
  const [saving, updatesaving] = React.useState<boolean>(false);
  const [diabledExemptions, setDisableExemptions] = React.useState<boolean>(
    true,
  );
  const [vefifyTaxRate, setVerifyTaxRate] = React.useState<number>(0);
  const [taxAmount, updateTaxAmount] = React.useState<string>('');
  const [exemptReasons, setExemptReasons] = React.useState<ExemptionDataInfo[]>(
    [],
  );
  const [exemptDetails, setExemptDetails] = React.useState<string>('');
  const [exemptImage, setExemptImage] = React.useState<string>(
    TransactionlogoUrl,
  );
  const [fileName, setFileName] = React.useState<string>('');
  React.useEffect(() => {
    activityLog = [];
    setVerifyTaxRate(taxRateCalc(taxRate));
    updateTaxAmount(taxCalculation(purchasePrice, taxRate));
    if (exemptionData) {
      const {
        exemptReasons = [],
        exemptDetails = '',
        exemptImage = '',
        fileName = '',
        verified = false,
      } = exemptionData;
      updateExemptions(
        (verified && status === 'COMPLETED') || status === 'PENDING'
          ? false
          : true,
      );
      const exemptionsReasonsTemp =
        exemptReasons && exemptReasons.length ? exemptReasons : [];
      const showExemptionButton = exemptReasons.filter(
        (e: any) => e.checked === true,
      );
      setDisableExemptions(showExemptionButton.length > 0 ? false : true);
      setExemptReasons(exemptionsReasonsTemp);
      setExemptDetails(exemptDetails);
      setExemptImage(exemptImage ? exemptImage : TransactionlogoUrl);
      setFileName(fileName);
    }
    // eslint-disable-next-line
  }, [purchasePrice, taxRate, exemptionData]);

  const getActivityLog = () => {
    if (status !== 'COMPLETED') {
      activityLog.push({
        transactionId: transactionId,
        firebaseId: userFirebaseId,
        item: `Response`,
        description: 'for Verify Tax Calculations task has been submitted by',
      });
    } else {
      activityLog.push({
        transactionId: transactionId,
        firebaseId: userFirebaseId,
        item: `Response`,
        description: 'for Verify Tax Calculations task has been updated by',
      });
    }
    return activityLog;
  };

  const submitChecklistData = () => {
    updatesaving(true);
    const checklistVariables = {
      transactionId: transactionId,
      status: 'COMPLETED',
      userType:
        exemptionData && exemptionData.userType ? exemptionData.userType : '',
      completedAt: moment().format('YYYY-MM-DD'),
      firebaseId: userFirebaseId,
      assignmentChecklistKey: 'verifyTaxCalculations',
      activityLog: getActivityLog(),
    };
    if (showExemptions) {
      const exemptionPayment: PaymentProps[] = payments.filter(
        (payment: PaymentProps) => {
          return payment.costItem === 'Transfer Tax Amount';
        },
      );
      if (exemptionPayment && exemptionPayment.length) {
        Object.assign(checklistVariables, {
          isDelete: taxExemption ? false : true,
          cost: taxExemption,
          costItem: taxExemption ? 'Exemption Cost' : 'Transfer Tax Amount',
          paymentId: exemptionPayment[0]._id,
        });
      }
      const mappedExemptions = exemptReasons.map((a: ExemptionDataInfo) => {
        return {
          id: a.id,
          checked: a.checked,
        };
      });
      Object.assign(checklistVariables, {
        exemptionReasons: mappedExemptions,
        exemptionDetails: exemptDetails,
        exemptionDocuments: [
          {
            name: fileName,
            src: exemptImage === TransactionlogoUrl ? '' : exemptImage,
            uploadedDate: moment().format('YYYY-MM-DD'),
          },
        ],
      });
    } else {
      Object.assign(checklistVariables, {
        taxAmount: parseFloat(taxAmount.replace(/[&\\#,+()$~%'":*?<>{}]/g, '')),
        verified: true,
      });
    }
    updateCheckList({
      variables: checklistVariables,
    }).then((res: any) => {
      if (res.data.updateTransactionChecklist.success) {
        const {
          updateTransactionChecklist: { transaction },
        } = res.data;
        const { assignmentChecklist, activityLog, pendingTasks } = transaction;
        loadChecklistData(assignmentChecklist, pendingTasks);
        getActivityLogData(activityLog);
        updatesaving(false);
        if (showExemptions) {
          window.location.reload();
        } else {
          setTimeout(onModalClose, 100);
        }
      }
    });
  };

  const handleExemptReasons = (event: any, index: number) => {
    if (event.target.name.startsWith('exempt')) {
      if (index !== undefined) {
        let checked = event.target.checked;
        let t_exemptList = [...exemptReasons];
        if (checked) {
          Object.assign(t_exemptList[index], {
            id: event.target.value,
            checked: true,
          });

          setDisableExemptions(false);
        } else {
          Object.assign(t_exemptList[index], {
            id: event.target.value,
            checked: false,
          });
        }
        setExemptReasons(t_exemptList);
        const filteredArray = t_exemptList.filter(e => e.checked === true);
        if (filteredArray.length > 0) {
          setDisableExemptions(false);
        } else {
          setDisableExemptions(true);
        }
      }
    }
  };

  const onImageChange = (event: any) => {
    if (event.target.files && event.target.files[0]) {
      setFileName(event.target.files[0].name);
      const reader = new FileReader();
      reader.onload = (e: any) => {
        setExemptImage(e.target.result);
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const deleteIamge = () => {
    setExemptImage(TransactionlogoUrl);
    setFileName('');
  };

  return (
    <Dialog
      title="Verify Tax Calculations"
      onClose={onModalClose}
      isCenter={true}
      showBackButton={showExemptions}
      onBackClick={() => {
        updateExemptions(false);
      }}>
      {!showExemptions ? (
        <ModalContainer>
          <ChecklistText>{taxInst}</ChecklistText>
          <VerifyBox>
            <VerifyCard>
              <TaxValue>{'Purchase Price'} </TaxValue>
              <EqualValue>=</EqualValue>
              <TaxText> {purchasePrice}</TaxText>
            </VerifyCard>
            <VerifyCard>
              <TaxValue>{'Tax Rate'} </TaxValue>
              <EqualValue>= </EqualValue>
              <TaxText> {vefifyTaxRate}</TaxText>
            </VerifyCard>
            <VerifyCard>
              <TaxValue>{'Tax Amount'} </TaxValue>
              <EqualValue>= </EqualValue>
              <TaxText>Purchase Price * Tax Rate</TaxText>
            </VerifyCard>
            <VerifyCard>
              <TaxValue></TaxValue>
              <EqualValue>= </EqualValue>
              <TaxText>
                {`${purchasePrice}`} * {vefifyTaxRate}
              </TaxText>
            </VerifyCard>
            <VerifyCard>
              <TaxValue></TaxValue>
              <EqualValue>= </EqualValue>
              <TaxText> ${taxAmount}</TaxText>
            </VerifyCard>
          </VerifyBox>
        </ModalContainer>
      ) : (
        <ModalContainer>
          <ExemptInfoBox>
            <ChecklistPrimaryText>
              Please select the reasons for tax exemption.
            </ChecklistPrimaryText>
            <ExemptContainer>
              {exemptReasons.map((exempt, i) => {
                return (
                  <ExemptBox key={i}>
                    <Checkbox
                      label={exempt.exemption}
                      name={`exempt[${i}]`}
                      size="default"
                      value={exempt.id}
                      checked={exempt.checked}
                      onChange={e => handleExemptReasons(e, i)}
                    />
                  </ExemptBox>
                );
              })}
            </ExemptContainer>
            <ExemptTextArea>
              <TextArea
                label="Details for Exemption Claimed"
                placeholder="Enter Details (any)"
                name="exemptdetails"
                value={exemptDetails ? exemptDetails : ''}
                onChange={e => {
                  setExemptDetails(e.target.value);
                }}
              />
            </ExemptTextArea>
            <InputCard>
              <Uploader
                label=" Upload the Documentation"
                profileImage={exemptImage}
                fileName={fileName}
                chooseText="Browse File"
                onChange={e => onImageChange(e)}
                borderRadius="8px"
                onImageDelete={deleteIamge}
              />
            </InputCard>
            {taxExemption ? (
              <ChecklistPrimaryText>
                Note: ${taxExemption} will be charged as a minimum tax.
              </ChecklistPrimaryText>
            ) : null}
          </ExemptInfoBox>
        </ModalContainer>
      )}
      <Footer>
        {saving && (
          <SavingIndicator>
            <FontAwesomeIcon
              color={primaryColor}
              icon={faCircleNotch}
              size="lg"
              spin
            />
          </SavingIndicator>
        )}
        <CancelButton>
          <Button variant="text" size="medium" onClick={onModalClose}>
            Cancel
          </Button>
        </CancelButton>
        {!showExemptions && (
          <ExemptButtonContainer disabled={saving}>
            <Button
              variant="secondary"
              size="medium"
              width="100%"
              type="submit"
              onClick={() => {
                updateExemptions(true);
              }}>
              Exempt
            </Button>
          </ExemptButtonContainer>
        )}
        <ChecklistButtonContainer
          disabled={!showExemptions ? saving : saving || diabledExemptions}>
          <Button
            variant="primary"
            size="medium"
            width="100%"
            type="submit"
            onClick={submitChecklistData}>
            {!showExemptions ? ' Verify' : 'Submit'}
          </Button>
        </ChecklistButtonContainer>
      </Footer>
    </Dialog>
  );
}
